/*
 * Easemate - fast add to cart + cart drawer polish + 15% offer line (v7.9)
 * Adds the main product AND its Kaching free gift in ONE /cart/add request,
 * instead of Kaching adding the gift in a second round trip after the first add.
 * Gift payload is built from Kaching's own live config (window.__kachingCartProps),
 * so it follows any rule changes made in the Kaching app.
 * Safety net: if the gift ever lands in the cart at full price, it is removed and
 * Kaching's normal flow adds it back. To roll back: delete the <script> tag in layout/theme.liquid.
 * v7.9: hides Kaching's "You save $X" badge and "Discounts 100% -$X" row so cart savings match checkout
 *       (looser text matching than v7.8, which missed them because Kaching renders the words without spaces).
 */
(() => {
  if (window.__emFastAtc) return;
  window.__emFastAtc = true;

  // Fallback when a Kaching rule item has no fixed variant (Crystal Hair Eraser -> its only variant)
  const GIFT_VARIANT_FALLBACK = { 8361533309111: 46856155988151 };
  const MARK = 'em';

  const props = () => window.__kachingCartProps || {};
  let cartLines = null;
  const lines = () => cartLines || (props().cart && props().cart.items) || [];

  function giftsFor(productId) {
    const rules = (props().autoAtcRules && props().autoAtcRules.rules) || [];
    const out = [];
    for (const r of rules) {
      if (!r.isEnabled || !r.action || r.action.type !== 'auto_add') continue;
      if (!r.triggers || r.triggers.length !== 1) continue;
      const t = r.triggers[0];
      if (t.conditionType !== 'product' || t.thresholdType !== 'any') continue;
      if (!(t.values || []).some((v) => Number(v.productId) === productId && !v.variantIds)) continue;
      for (const it of r.action.items || []) {
        const variantId = it.variantId || GIFT_VARIANT_FALLBACK[it.productId];
        if (!variantId) continue;
        if (lines().some((l) => Number(l.product_id) === Number(it.productId))) continue;
        const payload = {
          isAutoAtcGift: true,
          autoAtcGiftPayload: {
            ruleId: r.id,
            actionItemId: it.actionItemId,
            productId: it.productId,
            qty: it.quantity || 1,
            discountAmount: it.discountAmount,
            discountType: it.discountType,
            hash: it.hash,
            autoAtcUniqueId: MARK + Math.random().toString(36).slice(2, 6),
          },
        };
        out.push({ variantId, qty: it.quantity || 1, prop: JSON.stringify(payload) });
      }
    }
    return out;
  }

  const origFetch = window.fetch;

  /* ---- Cart drawer polish (v7.3) ----
   * 1) 90-day guarantee line under the Kaching checkout button (pure CSS, so Kaching re-renders can't remove it).
   * 2) While the first item is being added, show "Adding to your cart..." instead of an empty "My cart • 0" drawer.
   */
  const css = `
#kaching-cart .kaching-cart__footer > .kaching-cart__checkout-button{order:1}
#kaching-cart .kaching-cart__footer > .kaching-cart__continue-shopping{order:3}
#kaching-cart .kaching-cart__footer > .em15-cart{order:0;display:flex;align-items:center;justify-content:space-between;gap:8px;border:1px solid #E8DFD8;background:#FAF6F1;border-radius:10px;padding:9px 12px;font:500 13px/1.35 Inter,system-ui,sans-serif;color:#2A2127;cursor:pointer;text-align:left}
#kaching-cart .kaching-cart__footer > .em15-cart b{color:#9E4F63}
#kaching-cart .kaching-cart__footer > .em15-cart.is-on{cursor:default;background:#F2F8F3;border-color:#BFD8C4;color:#2E6B45}
#kaching-cart .kaching-cart__footer::after{order:2;content:"\\1F512  90-day money-back guarantee\\A Not for you? Full refund within 90 days of delivery \\00B7  1-year warranty";white-space:pre-line;display:block;text-align:center;font:400 12.5px/1.45 Inter,system-ui,sans-serif;color:#2E6B45;background:#F2F8F3;border:1px solid #BFD8C4;border-radius:10px;padding:8px 10px}
html.em-cart-loading #kaching-cart .kaching-cart__contents,html.em-cart-loading #kaching-cart .kaching-cart__footer,html.em-cart-loading #kaching-cart .kaching-cart__heading{opacity:0!important;pointer-events:none}
html.em-cart-loading #kaching-cart .kaching-cart__inner::before{content:"";position:absolute;left:50%;top:calc(45% - 34px);width:26px;height:26px;margin-left:-13px;border:3px solid #E8DFD8;border-top-color:#9E4F63;border-radius:50%;animation:em-spin .8s linear infinite}
html.em-cart-loading #kaching-cart .kaching-cart__inner::after{content:"Adding to your cart\\2026";position:absolute;left:0;right:0;top:45%;text-align:center;font:500 15px/1.4 Inter,system-ui,sans-serif;color:#5C5257}
#kaching-cart [data-em-hide]{display:none!important}
@keyframes em-spin{to{transform:rotate(360deg)}}`;
  const styleEl = document.createElement('style');
  styleEl.id = 'em-cart-polish';
  styleEl.textContent = css;
  (document.head || document.documentElement).appendChild(styleEl);

  const root = document.documentElement;
  let loadTimer = null;
  let cartObs = null;
  function stopLoading() {
    root.classList.remove('em-cart-loading');
    clearTimeout(loadTimer);
  }
  function startLoading() {
    root.classList.add('em-cart-loading');
    clearTimeout(loadTimer);
    loadTimer = setTimeout(stopLoading, 5000);
    const k = document.getElementById('kaching-cart');
    if (!k || cartObs) return;
    cartObs = new MutationObserver(() => {
      if (root.classList.contains('em-cart-loading') && k.querySelector('.kaching-cart__contents .kaching-cart-item')) stopLoading();
    });
    cartObs.observe(k, { childList: true, subtree: true, characterData: true, attributes: true });
  }

  /* v7.9: hide Kaching's "You save $X" badge and the "Discounts 100% -$X" row.
   * Shopify checkout only shows the $32 gift saving, so these made the deal look like it shrank at checkout.
   * Matched by visible text (Kaching class names can change). Each match must contain exactly one $ amount,
   * so the climb stops before it reaches the product price ($249/$189) or the Subtotal line. */
  const txt = (el) => (el.textContent || '').replace(/\s+/g, ' ').trim();
  const dollars = (t) => (t.match(/\$/g) || []).length;
  const RULES = [
    { test: (t) => /you\s*save/i.test(t) && /\$\s?\d/.test(t), max: 40 },
    { test: (t) => /^discounts?/i.test(t) && /-\s?\$\s?\d/.test(t) && !/subtotal|checkout|total/i.test(t), max: 60 },
  ];
  function hideSavings() {
    const k = document.getElementById('kaching-cart');
    if (!k) return;
    for (const rule of RULES) {
      const ok = (el) => {
        const t = txt(el);
        return t.length <= rule.max && dollars(t) === 1 && rule.test(t);
      };
      k.querySelectorAll('*').forEach((el) => {
        if (el.closest('[data-em-hide]') || el.closest('.em15-cart')) return;
        if (!ok(el)) return;
        let top = el;
        while (top.parentElement && top.parentElement !== k && ok(top.parentElement)) top = top.parentElement;
        top.setAttribute('data-em-hide', '');
      });
    }
  }

  /* 15% email offer line in the cart drawer (shown before checkout) */
  const EM15_URL = '/products/ipl-hair-removal-device#em15';
  const em15On = () => { try { return localStorage.getItem('em15') === '1'; } catch (e) { return false; } };
  function em15Click() {
    if (em15On()) return;
    const f = document.getElementById('em15');
    if (f) {
      try { window.kachingCartApi && window.kachingCartApi.closeCart(); } catch (e) {}
      setTimeout(() => {
        f.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const i = f.querySelector('input[type=email]');
        if (i) setTimeout(() => i.focus({ preventScroll: true }), 400);
      }, 250);
    } else {
      location.href = EM15_URL;
    }
  }
  function ensureEm15() {
    const footer = document.querySelector('#kaching-cart .kaching-cart__footer');
    if (!footer) return;
    let el = footer.querySelector(':scope > .em15-cart');
    if (!el) {
      el = document.createElement('div');
      el.className = 'em15-cart';
      el.setAttribute('role', 'button');
      el.tabIndex = 0;
      el.addEventListener('click', em15Click);
      el.addEventListener('keydown', (e) => { if (e.key === 'Enter') em15Click(); });
      footer.appendChild(el);
    }
    const on = em15On();
    if (el.dataset.on !== String(on)) {
      el.dataset.on = String(on);
      el.classList.toggle('is-on', on);
      el.innerHTML = on
        ? '<span>✓ Your <b style="color:#2E6B45">15% off</b> (WELCOME15) is applied at checkout</span>'
        : '<span><b>Get 15% off</b> — enter your email to unlock your code</span><span aria-hidden="true">›</span>';
    }
  }
  function tidyCart() {
    try { ensureEm15(); } catch (e) {}
    try { hideSavings(); } catch (e) {}
  }
  let em15Raf = 0;
  const em15Obs = new MutationObserver(() => { cancelAnimationFrame(em15Raf); em15Raf = requestAnimationFrame(tidyCart); });
  const em15Start = () => {
    const k = document.getElementById('kaching-cart');
    if (!k) return false;
    em15Obs.observe(k, { childList: true, subtree: true, characterData: true });
    tidyCart();
    return true;
  };
  if (!em15Start()) {
    let tries = 0;
    const t = setInterval(() => { if (em15Start() || ++tries > 40) clearInterval(t); }, 250);
  }

  async function verifyGifts() {
    try {
      const cart = await origFetch('/cart.js').then((r) => r.json());
      cartLines = cart.items || [];
      for (const l of cartLines) {
        const p = l.properties && l.properties._kaching_cart;
        if (p && p.includes('"autoAtcUniqueId":"' + MARK) && l.final_line_price > 0) {
          // Gift was not discounted -> remove it; Kaching's own rule re-adds it correctly.
          await origFetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: l.key, quantity: 0 }),
          });
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      }
    } catch (e) {
      console.warn('[em-fast-atc] verify failed', e);
    }
  }

  window.fetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = ((init && init.method) || 'GET').toUpperCase();

      // Keep a fresh copy of cart lines from any /cart.js read
      if (method === 'GET' && /\/cart\.js(\?|$)/.test(url)) {
        return origFetch.call(this, input, init).then((res) => {
          res.clone().json().then((c) => { if (c && c.items) cartLines = c.items; }).catch(() => {});
          return res;
        });
      }

      // Cart changed elsewhere (remove, qty change, clear): refresh our copy from the response
      if (method === 'POST' && /\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(url)) {
        return origFetch.call(this, input, init).then((res) => {
          res.clone().json().then((c) => { if (c && c.items) cartLines = c.items; }).catch(() => {});
          return res;
        });
      }

      if (method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url) && !/kaching/.test(url) && init && init.body instanceof FormData) {
        const fd = init.body;
        const id = fd.get('id');
        const form = id && document.querySelector('product-form-component input[name="id"][value="' + id + '"]');
        const pf = form && form.closest('product-form-component');
        const pid = pf && Number(pf.dataset.productId);
        const gifts = pid ? giftsFor(pid) : [];
        if (lines().length === 0) startLoading();
        if (gifts.length) {
          const nfd = new FormData();
          nfd.append('items[0][id]', id);
          nfd.append('items[0][quantity]', fd.get('quantity') || '1');
          for (const [k, v] of fd.entries()) {
            const m = k.match(/^properties\[(.+)\]$/);
            if (m) nfd.append('items[0][properties][' + m[1] + ']', v);
          }
          gifts.forEach((g, i) => {
            nfd.append('items[' + (i + 1) + '][id]', String(g.variantId));
            nfd.append('items[' + (i + 1) + '][quantity]', String(g.qty));
            nfd.append('items[' + (i + 1) + '][properties][_kaching_cart]', g.prop);
          });
          const sections = fd.getAll('sections').filter(Boolean);
          if (sections.length) nfd.append('sections', sections[sections.length - 1]);
          const newInit = Object.assign({}, init, { body: nfd });
          return origFetch.call(this, input, newInit).then((res) => {
            if (!res.ok) stopLoading();
            setTimeout(verifyGifts, 1500);
            return res;
          }, (err) => { stopLoading(); throw err; });
        }
      }
    } catch (e) {
      console.warn('[em-fast-atc]', e);
    }
    return origFetch.call(this, input, init);
  };
})();
